export type ProxySeoSlug =
  | 'buy-proxies'
  | 'residential-proxies'
  | 'mobile-proxies'
  | 'isp-proxies'
  | 'datacenter-proxies';

export interface ProxySeoCopy {
  title: string;
  description: string;
  h1: string;
  subtitle: string;
  intro: string;
  points: string[];
  faqs: { q: string; a: string }[];
}

export const PROXY_SEO_PAGES: Record<
  ProxySeoSlug,
  { path: string; en: ProxySeoCopy; ar: ProxySeoCopy }
> = {
  'buy-proxies': {
    path: '/buy-proxies',
    en: {
      title: 'Buy Proxies | Residential, Mobile, ISP & Datacenter | Rocket Proxy',
      description:
        'Buy proxies from Rocket Proxy. Residential, mobile, ISP, and datacenter proxies with HTTP and SOCKS5, instant delivery, and one account.',
      h1: 'Buy Proxies',
      subtitle:
        'Choose a proxy type, fund one account, and start using residential, mobile, ISP, or datacenter proxies.',
      intro:
        'Rocket Proxy is a proxy shop for teams that need to buy proxies without juggling several vendors. One wallet covers residential, mobile, ISP, and datacenter products, with HTTP and SOCKS5 delivered in the dashboard after checkout.',
      points: [
        'Buy proxies from one catalog: residential, mobile, ISP, and datacenter.',
        'HTTP and SOCKS5 connections, with country and city targeting where the product supports it.',
        'Instant delivery after purchase, plus API access for teams that automate orders.',
        'Balance, invoices, and support stay in the same Rocket Proxy account.',
      ],
      faqs: [
        { q: 'Can I buy proxies without a monthly contract?', a: 'Yes. You pay for the product you need, then use it from your account. There is no platform subscription required to check out.' },
        { q: 'Which protocols are available?', a: 'Most Rocket Proxy products expose HTTP and SOCKS5. Ports and logins appear in your dashboard after purchase.' },
        { q: 'How fast is delivery?', a: 'Credentials are issued in the account as soon as payment is confirmed.' },
      ],
    },
    ar: {
      title: 'شراء بروكسي | سكني، موبايل، ISP ومركز بيانات | Rocket Proxy',
      description:
        'اشترِ بروكسي من Rocket Proxy: سكني، موبايل، ISP، ومركز بيانات. HTTP وSOCKS5، تسليم فوري، وحساب واحد.',
      h1: 'شراء بروكسي',
      subtitle: 'اختر نوع البروكسي، اشحن حسابك، وابدأ باستخدام سكني أو موبايل أو ISP أو مركز بيانات.',
      intro:
        'Rocket Proxy متجر بروكسي لفريق يحتاج يشتري بروكسي بدون تشتيت بين موردين. محفظة واحدة تغطي السكني والموبايل وISP ومركز البيانات، مع HTTP وSOCKS5 داخل لوحة التحكم بعد الدفع.',
      points: [
        'اشترِ بروكسي من كتالوج واحد: سكني، موبايل، ISP، ومركز بيانات.',
        'اتصال HTTP وSOCKS5، مع استهداف الدولة والمدينة حسب المنتج.',
        'تسليم فوري بعد الشراء، وواجهة API للطلبات الآلية.',
        'الرصيد والفواتير والدعم تبقى في نفس حساب Rocket Proxy.',
      ],
      faqs: [
        { q: 'هل أقدر أشتري بروكسي بدون اشتراك شهري؟', a: 'نعم. تدفع ثمن المنتج الذي تحتاجه ثم تستخدمه من حسابك، بدون اشتراك منصة إجباري.' },
        { q: 'ما البروتوكولات المتوفرة؟', a: 'أغلب المنتجات تعرض HTTP وSOCKS5. المنافذ وبيانات الدخول تظهر في لوحة التحكم بعد الشراء.' },
        { q: 'كم يستغرق التسليم؟', a: 'تُصدر بيانات الاتصال في الحساب فور تأكيد الدفع.' },
      ],
    },
  },
  'residential-proxies': {
    path: '/residential-proxies',
    en: {
      title: 'Buy Residential Proxies | Rocket Proxy',
      description:
        'Buy residential proxies from Rocket Proxy. Real-user IPs, HTTP and SOCKS5, traffic in GB, and instant access from your account.',
      h1: 'Residential Proxies',
      subtitle: 'Residential IPs for accounts, scraping, and multi-location work that needs a normal home connection.',
      intro:
        'Buy residential proxies when the job should look like a real household connection. Rocket Proxy sells residential traffic in GB, with HTTP and SOCKS5 on the same endpoint and country targeting from the purchase screen.',
      points: [
        'Traffic sold in GB from a residential pool.',
        'HTTP and SOCKS5 on the same endpoint.',
        'Country targeting and rotation options from the purchase screen.',
        'Usage and remaining traffic stay visible in your account.',
      ],
      faqs: [
        { q: 'What are residential proxies used for?', a: 'They are a fit when a site expects a home ISP address rather than a datacenter IP.' },
        { q: 'Do residential proxies support SOCKS5?', a: 'Yes. Rocket Proxy residential endpoints include HTTP and SOCKS5 ports.' },
        { q: 'How is residential traffic billed?', a: 'You buy a GB package, then create proxies inside that package from your account.' },
      ],
    },
    ar: {
      title: 'شراء بروكسي سكني | Rocket Proxy',
      description:
        'اشترِ بروكسي سكني من Rocket Proxy. عناوين سكنية، HTTP وSOCKS5، باقات بالجيجا، وتفعيل فوري من حسابك.',
      h1: 'بروكسي سكني',
      subtitle: 'عناوين سكنية للحسابات والعمل من مواقع متعددة عندما تحتاج اتصال بيت عادي.',
      intro:
        'اشترِ بروكسي سكني عندما المهمة تحتاج اتصال يشبه منزل حقيقي. الترافيك يُباع بالجيجا، مع HTTP وSOCKS5 على نفس النقطة واختيار الدولة من شاشة الشراء.',
      points: [
        'الترافيك يُباع بالجيجابايت من شبكة سكنية.',
        'HTTP وSOCKS5 على نفس النقطة.',
        'اختيار الدولة وخيارات التدوير من شاشة الشراء.',
        'الاستهلاك والمتبقي يظهران داخل حسابك.',
      ],
      faqs: [
        { q: 'متى أستخدم بروكسي سكني؟', a: 'عندما الموقع يتوقع عنوان مزوّد منزلي وليس IP مركز بيانات.' },
        { q: 'هل السكني يدعم SOCKS5؟', a: 'نعم. نقاط Rocket Proxy السكنية تتضمن منافذ HTTP وSOCKS5.' },
        { q: 'كيف يُحتسب الترافيك؟', a: 'تشتري باقة بالجيجا ثم تنشئ البروكسيات داخل الباقة من حسابك.' },
      ],
    },
  },
  'mobile-proxies': {
    path: '/mobile-proxies',
    en: {
      title: 'Buy Mobile Proxies | Rocket Proxy',
      description:
        'Buy mobile proxies from Rocket Proxy. Rotating 4G/5G IPs for sessions that need a carrier connection, with fast delivery.',
      h1: 'Mobile Proxies',
      subtitle: 'Carrier IPs when a residential or datacenter address is not the right fit.',
      intro:
        'Buy mobile proxies when the session should come from a 4G or 5G carrier. Rocket Proxy rotating mobile products sit next to your residential and ISP orders in the same account.',
      points: [
        'Rotating mobile proxies on carrier networks.',
        'Useful for sessions that expect a phone connection.',
        'Managed from the same Rocket Proxy account as your other products.',
        'Host, ports, and login details are issued after checkout.',
      ],
      faqs: [
        { q: 'How are mobile proxies different from residential?', a: 'Mobile proxies use carrier (4G/5G) IPs. Residential uses household ISP ranges.' },
        { q: 'Do you sell rotating mobile proxies?', a: 'Yes. Mobile products are sold as rotating carrier proxies from the catalog.' },
        { q: 'Can I keep mobile and residential in one wallet?', a: 'Yes. One Rocket Proxy balance funds every product type.' },
      ],
    },
    ar: {
      title: 'شراء بروكسي موبايل | Rocket Proxy',
      description:
        'اشترِ بروكسي موبايل من Rocket Proxy. عناوين 4G/5G دوّارة للجلسات التي تحتاج اتصال شركة اتصالات.',
      h1: 'بروكسي موبايل',
      subtitle: 'عناوين شركات الاتصالات عندما البروكسي السكني أو مركز البيانات غير مناسب.',
      intro:
        'اشترِ بروكسي موبايل عندما الجلسة لازم تطلع من شبكة 4G أو 5G. منتجات الموبايل الدوّارة تجلس بجانب السكني وISP في نفس الحساب.',
      points: [
        'بروكسي موبايل دوّار على شبكات الجوال.',
        'مناسب للجلسات التي تتوقع اتصال هاتف.',
        'يُدار من نفس حساب Rocket Proxy مع باقي المنتجات.',
        'الهوست والمنافذ وبيانات الدخول تُصدر بعد الدفع.',
      ],
      faqs: [
        { q: 'شو الفرق عن البروكسي السكني؟', a: 'الموبايل يستخدم IP شركة اتصالات 4G/5G، بينما السكني من نطاقات منازل.' },
        { q: 'هل تبيعون بروكسي موبايل دوّار؟', a: 'نعم. منتجات الموبايل تُباع كبروكسي دوّار من الكتالوج.' },
        { q: 'هل أستخدم نفس الرصيد؟', a: 'نعم. رصيد Rocket Proxy واحد يموّل كل أنواع المنتجات.' },
      ],
    },
  },
  'isp-proxies': {
    path: '/isp-proxies',
    en: {
      title: 'Buy ISP Proxies | Rocket Proxy',
      description:
        'Buy ISP proxies from Rocket Proxy. Static ISP addresses with datacenter speed and an ISP-assigned identity.',
      h1: 'ISP Proxies',
      subtitle: 'Static ISP proxies when you want a stable address that still looks like an internet provider.',
      intro:
        'Buy ISP proxies when you need a static address with ISP identity and datacenter-like speed. They stay assigned to you instead of rotating through a residential pool.',
      points: [
        'Static ISP proxies rather than a rotating residential pool.',
        'Faster than typical residential traffic, with an ISP-branded IP.',
        'Username, password, and ports shown in your account after purchase.',
        'Renew and manage the same proxy from the dashboard.',
      ],
      faqs: [
        { q: 'Are ISP proxies static?', a: 'Yes. You keep the same ISP address for the paid period instead of a rotating pool.' },
        { q: 'When should I pick ISP over datacenter?', a: 'Choose ISP when the target prefers an ISP-assigned identity with still-high speed.' },
        { q: 'What do I receive after buying ISP proxies?', a: 'Host, HTTP/SOCKS ports, username, and password in your Rocket Proxy account.' },
      ],
    },
    ar: {
      title: 'شراء بروكسي ISP | Rocket Proxy',
      description:
        'اشترِ بروكسي ISP من Rocket Proxy. عناوين ثابتة بسرعة مركز بيانات وبهوية مزوّد إنترنت.',
      h1: 'بروكسي ISP',
      subtitle: 'بروكسي ISP ثابت عندما تريد عنواناً مستقراً يبدو كمزوّد إنترنت.',
      intro:
        'اشترِ بروكسي ISP عندما تحتاج عنواناً ثابتاً بهوية مزوّد وسرعة قريبة من مركز البيانات. يبقى معيَّناً لك بدل ما يدور ضمن شبكة سكنية.',
      points: [
        'عناوين ISP ثابتة وليست شبكة سكنية دوّارة.',
        'أسرع من الترافيك السكني المعتاد، مع IP تابع لمزوّد.',
        'اسم المستخدم وكلمة المرور والمنافذ تظهر في حسابك بعد الشراء.',
        'تجديده وإدارته تتم من لوحة التحكم.',
      ],
      faqs: [
        { q: 'هل بروكسي ISP ثابت؟', a: 'نعم. تحتفظ بنفس عنوان ISP طوال فترة الاشتراك المدفوعة.' },
        { q: 'متى أختار ISP بدل مركز البيانات؟', a: 'عندما الهدف يفضّل هوية مزوّد إنترنت مع سرعة عالية.' },
        { q: 'ماذا أستلم بعد الشراء؟', a: 'الهوست ومنافذ HTTP/SOCKS واسم المستخدم وكلمة المرور داخل الحساب.' },
      ],
    },
  },
  'datacenter-proxies': {
    path: '/datacenter-proxies',
    en: {
      title: 'Buy Datacenter Proxies | Rocket Proxy',
      description:
        'Buy datacenter proxies from Rocket Proxy. Fast datacenter IPs for high-volume tasks, delivered in your account.',
      h1: 'Datacenter Proxies',
      subtitle: 'Datacenter speed for jobs that care more about throughput than a residential identity.',
      intro:
        'Buy datacenter proxies when throughput and low latency matter more than a household identity. Rocket Proxy issues host, port, and login details in the account right after payment.',
      points: [
        'Datacenter IPs for high-volume, latency-sensitive work.',
        'Straightforward host, port, and login details after checkout.',
        'Same balance and support as the rest of Rocket Proxy.',
        'Pair them with residential or mobile products in one wallet when a job needs mixed IPs.',
      ],
      faqs: [
        { q: 'When should I buy datacenter proxies?', a: 'Use them for high-volume tasks that need speed and do not require a residential or mobile IP.' },
        { q: 'Are datacenter proxies cheaper than residential?', a: 'Usually yes, because they run on datacenter ranges rather than household traffic sold in GB.' },
        { q: 'Can I mix datacenter with other types?', a: 'Yes. One Rocket Proxy account can hold datacenter, ISP, mobile, and residential products.' },
      ],
    },
    ar: {
      title: 'شراء بروكسي مركز بيانات | Rocket Proxy',
      description:
        'اشترِ بروكسي مركز بيانات من Rocket Proxy. عناوين سريعة للمهام كبيرة الحجم، وتُسلَّم داخل حسابك.',
      h1: 'بروكسي مركز بيانات',
      subtitle: 'سرعة مركز البيانات للأعمال التي يهمها الأداء أكثر من الهوية السكنية.',
      intro:
        'اشترِ بروكسي مركز بيانات عندما السرعة والتأخير المنخفض أهم من هوية منزلية. Rocket Proxy يصدر الهوست والمنفذ وبيانات الدخول داخل الحساب بعد الدفع.',
      points: [
        'عناوين مركز بيانات للمهام كبيرة الحجم والحساسة للتأخير.',
        'الهوست والمنفذ وبيانات الدخول تظهر بعد الشراء.',
        'نفس الرصيد والدعم لباقي خدمات Rocket Proxy.',
        'يمكنك جمعها مع سكني أو موبايل في نفس المحفظة إذا المهمة تحتاج أنواع IP مختلطة.',
      ],
      faqs: [
        { q: 'متى أشتري بروكسي مركز بيانات؟', a: 'للمهام كبيرة الحجم التي تحتاج سرعة ولا تتطلب IP سكني أو موبايل.' },
        { q: 'هل هو أرخص من السكني؟', a: 'عادة نعم، لأنه يعمل على نطاقات مراكز بيانات وليس ترافيك منازل يُباع بالجيجا.' },
        { q: 'هل أخلطه مع أنواع ثانية؟', a: 'نعم. حساب Rocket Proxy واحد يقدر يحمل مركز بيانات وISP وموبايل وسكني.' },
      ],
    },
  },
};
